import React, { useEffect, useState } from 'react';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';
import { AddOnsFormSchema } from './addOnsSchema';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { updateAddons } from '@custom/rtk-apis/Setting/addons/updateAddons';
import { getAddons } from '@custom/rtk-apis/Setting/addons/getAddons';
import { resetSuccess as resetUpdateAddOnsSuccess } from '@custom/store/slices/Setting/addOns/updateAddonsSlice'; // Assuming slice
import { resetUpdateAddons } from '@custom/store/slices/Setting/addOns/updateAddonsSlice';
import { Loader, SuccessModal, useTheme } from '@ohif/ui';
import './styles.css'; // Assuming you might want specific styles
import Header from '@custom/Header/Header';
import { customStyle } from '@custom/Utils/utils';
import { _routes } from '@custom/Utils/constants/routes';
import { useNavigate } from 'react-router-dom';

interface FeaturePoint {
  point: string;
}

export interface AddOn {
  _id?: string; // Optional for existing add-ons
  name: string;
  price: number;
  features: FeaturePoint[];
}

const emptyAddOn: AddOn = {
  name: '',
  price: 0,
  features: [{ point: '' }],
};

const AddOnsForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const getAddOnsResponse = useSelector((state: RootState) => state?.getAddonsResponse);
  const updateAddOnsResponse = useSelector((state: RootState) => state?.updateAddonsResponse);

  const [formAddOns, setFormAddOns] = useState<AddOn[]>([emptyAddOn]);
  const [removedAddOns, setRemovedAddOns] = useState<Array<{_id: string, delete: boolean}>>([]);
  const [successModal, setSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isDarkMode = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAddons());
  }, [dispatch]);

  useEffect(() => {
    console.log('getAddOnsResponse:', getAddOnsResponse);
    if (getAddOnsResponse?.success && getAddOnsResponse?.result?.data?.length > 0) {
      setIsLoading(false);
      setFormAddOns(getAddOnsResponse.result.data);
      // Reset removed add-ons when we get new data
      setRemovedAddOns([]);
    } else if (getAddOnsResponse?.success && getAddOnsResponse?.result?.data?.length === 0) {
      setIsLoading(false);
      setFormAddOns([emptyAddOn]);
      setRemovedAddOns([]);
    }
  }, [getAddOnsResponse]);

  useEffect(() => {
    if (updateAddOnsResponse?.success) {
      setSuccessModal(true);
      setTimeout(() => {
        setSuccessModal(false);
        dispatch(resetUpdateAddOnsSuccess());
        dispatch(getAddons());
        navigate(_routes.settings.pricing);
      }, 2000);
    }
  }, [updateAddOnsResponse, dispatch]);

  const handleSubmit = (values: { addOns: AddOn[] }) => {
    setIsLoading(true);
    // Combine current add-ons with removed add-ons marked for deletion
    const payload = [
      ...values.addOns,
      ...removedAddOns
    ];
    dispatch(updateAddons(payload))
      .unwrap()
      .catch((error) => {
        console.error('Error updating add-ons:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="page page__full" style={
      isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
    }>
      <Header title="Plan Settings" />
      <div className="page__body">
        <div className="btn__group custom__plan__btn" style={{
          justifyContent: "flex-end",
          alignItems: "center",
        }}>
          <button
            className='btn btn__white'
            onClick={()=>{
              navigate(_routes.settings.pricing);
            }}
          >
            Preview credits plan
          </button>
        </div>
        <div className="details">
          <div className='form form--innersec'>
            <section className="form__section">
              <h2 className="form__section__title">Add-Ons Settings</h2>
              <Formik
                initialValues={{ addOns: formAddOns }}
                validationSchema={AddOnsFormSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, handleChange, handleBlur, errors, touched }) => (
                  <Form>
                    <FieldArray name="addOns">
                      {({ remove: removeAddOn, push: pushAddOn }) => (
                        <>
                          {values.addOns.map((addOn, addOnIndex) => (
                            <div key={addOn._id || addOnIndex} className="add-on-item-container">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3 className='form__section__subtitle' style={{ fontWeight: 600 }}>{addOn.name || 'New Add-On'}</h3>
                                {values.addOns.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn__danger remove-plan-btn" // Reusing class for style consistency
                                    onClick={() => {
                                      // If the add-on has an ID, add it to removedAddOns before removing
                                      if (addOn._id) {
                                        setRemovedAddOns(prev => [...prev, { _id: addOn._id, delete: true }]);
                                      }
                                      removeAddOn(addOnIndex);
                                    }}
                                  >
                                    Remove Add-On
                                  </button>
                                )}
                              </div>

                              <div className="formRow formRow__2">
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label" htmlFor={`addOns.${addOnIndex}.name`}>
                                      Add-On Name
                                      <span className="required-asterisk">*</span>
                                    </label>
                                    <input
                                      id={`addOns.${addOnIndex}.name`}
                                      name={`addOns.${addOnIndex}.name`}
                                      type="text"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.addOns[addOnIndex].name}
                                      className="form__input form__input--sm"
                                    />
                                    <ErrorMessage name={`addOns.${addOnIndex}.name`} component="div" className="error text-error" />
                                  </div>
                                </div>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label" htmlFor={`addOns.${addOnIndex}.price`}>
                                      Price
                                      <span className="required-asterisk">*</span>
                                    </label>
                                    <input
                                      id={`addOns.${addOnIndex}.price`}
                                      name={`addOns.${addOnIndex}.price`}
                                      type="number"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.addOns[addOnIndex].price}
                                      className="form__input form__input--sm"
                                    />
                                    <ErrorMessage name={`addOns.${addOnIndex}.price`} component="div" className="error text-error" />
                                  </div>
                                </div>
                              </div>

                              {/* Dynamic Bullet Points for Features */}
                              <div className="form__Field" style={{ marginTop: '1rem' }}>
                                <label className="form__label">Features / Bullet Points <span className="required-asterisk">*</span></label>
                                <FieldArray name={`addOns.${addOnIndex}.features`}>
                                  {({ remove: removeFeature, push: pushFeature }) => (
                                    <>
                                      {values.addOns[addOnIndex].features.map((feature, featureIndex) => (
                                        <>
                                        <div key={featureIndex} className="feature-point-item" style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                          <input
                                            name={`addOns.${addOnIndex}.features.${featureIndex}.point`}
                                            type="text"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={feature.point}
                                            placeholder="Enter feature description"
                                            className="form__input form__input--sm"
                                            style={{ flexGrow: 1, marginRight: '0.5rem' }}
                                          />
                                          {values.addOns[addOnIndex].features.length > 1 && values.addOns[addOnIndex]._id !== "AI_SERVICE"  && (
                                            <button
                                              type="button"
                                              className="btn btn__danger btn__small-icon" // Assuming a class for small icon buttons, or adjust as needed
                                              onClick={() => removeFeature(featureIndex)}
                                              aria-label="Remove feature point"
                                              title="Remove feature point"
                                            >
                                              X
                                            </button>
                                          )}
                                        </div>
                                        <ErrorMessage name={`addOns.${addOnIndex}.features.${featureIndex}.point`} component="div" className="error text-error" />
                                        </>
                                      ))}
                                      {/* Display array-level errors for features (e.g., 'at least one feature is required') */}
                                      {typeof errors.addOns?.[addOnIndex]?.features === 'string' && (
                                        <div className="error text-error">{errors.addOns[addOnIndex].features}</div>
                                      )}
                                      <button
                                        type="button"
                                        className="btn btn__primary btn__outlined"
                                        onClick={() => pushFeature({ point: '' })}
                                        style={{ marginTop: '0.5rem' }}
                                      >

                                        + Add More Features
                                      </button>
                                    </>
                                  )}
                                </FieldArray>
                              </div>
                              {addOnIndex < values.addOns.length - 1 && <hr style={{ margin: '2rem 0' }} />}
                            </div>
                          ))}
                          <div className="add-plan-button-container">
                            <button
                              type="button"
                              className="btn btn__secondary add-more-btn" // Reusing class for style consistency
                              onClick={() => pushAddOn(emptyAddOn)}
                              style={{ marginTop: '1rem' }}
                            >
                              + Add More
                            </button>
                          </div>
                        </>
                      )}
                    </FieldArray>

                    <div style={{ marginTop: '2rem' }}>
                      <button
                        type="submit"
                        className="btn btn__primary"
                        // disabled={isLoading || !dirty || !isValid} // Add dirty and isValid checks from Formik props if needed
                        disabled={isLoading}
                      >
                        {isLoading ? 'Updating...' : 'Update Add-Ons'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
              <Loader isLoaderOpen={isLoading} />
              <SuccessModal
                isSuccessModalOpen={successModal}
                title="Add-Ons Updated Successfully!"
                handleCloseModal={() => setSuccessModal(false)}
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOnsForm;
